import "./styles/FileInput.sass";
import * as Dropzone from "react-dropzone";
import { useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
interface IProps {
    inputName: string;
    value?: string;
    savedImagePath?: string | File;
    parentFunction: (file: File) => void;
    hideLabel?: boolean;
    children?: JSX.Element;
}

const FileInput = ({
    inputName,
    value = "",
    savedImagePath = "",
    hideLabel = false,
    children,
    parentFunction,
}: IProps) => {
    const { control, setValue } = useFormContext();
    // returns all values and methods from the Formik tag
    const savedImageIsFile = savedImagePath instanceof File;
    // Drop zone setup
    const onDropAccepted = useCallback((acceptedFiles: Array<File>) => {
        if (acceptedFiles.length) {
            handleFile(inputName, acceptedFiles[0]);
            setValue(inputName, acceptedFiles[0]);
        }
    }, []);
    const {
        getRootProps,
        getInputProps,
        isDragActive,
        fileRejections,
        acceptedFiles,
    } = Dropzone.useDropzone({
        onDropAccepted,
        maxFiles: 1,
        maxSize: 2000000,
        multiple: false,
        accept: {
            "image/jpeg": [],
            "image/png": [],
            "image/jpg": [],
            "image/webp": [],
            "text/gltf": [],
        },
    });
    /** When the user uploads a new file, transform it to a dataURL
     * and display it back
     */
    function uploadToURL(image: File): string {
        const imageBlob = URL.createObjectURL(image);
        return imageBlob;
    }
    const handleFile = (_inputName: string, file: File) => {
        // Formik sets its own form value, but it doesn't reflec on the DOM input
        if (file !== undefined) {
            if (parentFunction) {
                parentFunction(file);
            }
        } else {
            return;
        }
    };
    return (
        <Controller
            name={inputName}
            control={control}
            defaultValue={value}
            render={() => (
                <div className="file__input" {...getRootProps()}>
                    {hideLabel ? (
                        ""
                    ) : (
                        <label htmlFor={inputName}>{inputName}</label>
                    )}
                    <input
                        data-cy={`file-input-${inputName}`}
                        name={inputName}
                        {...getInputProps()}
                        aria-label="Click here to upload your files"
                    />
                    <div
                        data-cy={`file-dropzone-${inputName}`}
                        className={`dropzone`}
                    >
                        {children && children}
                        {isDragActive ? (
                            <p>Drop the files here ...</p>
                        ) : acceptedFiles?.length ? (
                            <div className="fileinput__preview">
                                <p>
                                    Selected file:
                                    <br />
                                </p>
                                <div className="fileinput__preview__container">
                                    <img
                                        className="fileinput__preview__container__image"
                                        src={uploadToURL(acceptedFiles[0])}
                                    />
                                </div>
                            </div>
                        ) : savedImagePath !== "" && !savedImageIsFile ? (
                            <div className="fileinput__preview">
                                <p>
                                    Selected file:
                                    <br />
                                </p>
                                <div className="fileinput__preview__container">
                                    <img
                                        className="fileinput__preview__container__image"
                                        src={savedImagePath}
                                    />
                                </div>
                            </div>
                        ) : (
                            <p className="dropzone__text">
                                <span className="dropzone__text--bold">
                                    Drag and drop or click here{" "}
                                </span>
                                <br />
                                to upload your image
                            </p>
                        )}
                    </div>
                    {fileRejections.map(({ errors }, key: number) => {
                        return (
                            <p className="error-message" key={key}>
                                {errors[0].message}
                            </p>
                        );
                    })}
                </div>
            )}
        />
    );
};

export default FileInput;
